// src/EditPost.js
import React, { useState, useEffect, useRef } from "react";
import { Button, Form, Modal, Dropdown } from "react-bootstrap";
import { useDropzone } from "react-dropzone";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useNavigate, useParams } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { ToastContainer, toast } from "react-toastify";
import { BASE_URL, IMAGE_URL, security_key } from "../../../global";
import Select from "react-select";
import axios from "axios";
import LinearProgress from "@mui/material/LinearProgress";

const EditRssArticleForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const formRef = useRef(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [categoryOption, setCategoryOption] = useState([]);
    const [magazineOption, setMagazineOption] = useState([]);
    const [article, setArticle] = useState(null);
    const newTagRef = useRef(null);
    const [statusOption, setStatusOption] = useState([
        { value: 1, label: "Draft" },
        { value: 2, label: "Published" },
        { value: 3, label: "Archived" },
    ]);
    const [showPreview, setShowPreview] = useState(false);

    const [fullArticle, setFullArticle] = useState("");

    const getFullArticle = async (url) => {
        const response = await axios.post(BASE_URL + "API/apicall", {
            security_key: security_key,
            mode: "getFullArticle",
            url: url,
        });

        const output = response.data;

        if (output) {
            setFullArticle(output);
        }
    };

    useEffect(() => {
        fetchArticle();
    }, [id]);

    const fetchArticle = async () => {
        try {
            const response = await axios.post(BASE_URL + "API/apicall", {
                security_key: security_key,
                pk_ac_key: id,
                mode: "getRssFeed",
            });
            const output = response.data;

            if (output) {
                setArticle(output);
            }
        } catch (error) {
            toast.error("Error fetching article details.", { autoClose: 3000 });
        }
    };

    useEffect(() => {
        getAllCategory();
        getAllMagazine();
    }, []);

    const getAllCategory = async () => {
        const response = await axios.post(BASE_URL + "API/apicall", {
            security_key: security_key,
            mode: "getAllCategory",
        });

        const output = response.data;

        if (output.length > 0) {
            setCategoryOption(
                output.map((option) => ({
                    value: option.pk_cat_key,
                    label: option.cat_name,
                }))
            );
        }
    };

    const getAllMagazine = async () => {
        const response = await axios.post(BASE_URL + "API/apicall", {
            security_key: security_key,
            mode: "getAllMagazine",
        });

        const output = response.data;

        if (output.length > 0) {
            setMagazineOption(
                output.map((option) => ({
                    value: option.pk_mag_key,
                    label: option.mag_name,
                }))
            );
        }
    };

    const imgData = article?.img_data ? JSON.parse(article.img_data) : null;
    const conData = article?.con_data ? JSON.parse(article.con_data) : null;

    const initialValues = {
        pk_ac_key: article ? article.pk_ac_key : "",
        ac_name: article ? article.ac_name : "",
        ac_summary: article ? article.ac_summary : "",
        img_to_rem: imgData?.file_name,
        img_key: imgData?.pk_as_key,
        ac_img_new: "",
        ac_img_link: IMAGE_URL + imgData?.file_name,
        con_key: conData?.pk_as_key,
        ac_content: conData?.as_content,
        tags: article?.tags ? JSON.parse(article.tags) : [],
        newTag: article?.newTag || "",
        status: article ? article.status : "",
        fk_mag_key: article ? article.fk_mag_key : "",
        category: article?.cat_keys ? JSON.parse(article.cat_keys) : [],
        updated_by: sessionStorage.getItem("pk_user_key"),
        created_by: sessionStorage.getItem("pk_user_key"),
    };

    const validationSchema = Yup.object().shape({
        ac_name: Yup.string()
            .min(3, "Article Name must be at least 3 characters.")
            .max(250, "Article Name must be at most 250 characters.")
            // .matches(
            //     /^[a-zA-Z-:;\s]+$/,
            //     "Article Name should only contain letters, spaces, hyphens, colons, and semicolons, and should not start or end with a space."
            // )
            // .matches(/^[a-zA-Z][a-zA-Z\s]*[a-zA-Z]$/, "Article Name should only contain letters and spaces, and should not start or end with a space.")
            .required("Article Name is required.")
            .test("unique-link", "Article Name is already existing in the database", async function (value) {
                if (!value) return true;
                return await checkArticleTitle(value);
            }),
        ac_summary: Yup.string()
            .min(90, "Article Summary must be at least 90 character.")
            // .max(300, "Article Summary must be at most 300 characters.")
            // .matches(/^[^\s].*[^\s]$/, "Summary should not have leading or trailing spaces.")
            .required("Article Summary is required."),

        tags: Yup.array()
            .of(
                Yup.string()
                    .matches(/^[a-zA-Z0-9\s]+$/, "Only letters and numbers are allowed")
                    .required("Tag is required")
            )
            .default([]),
        newTag: Yup.string()
            .when("tags", (tags, schema) => {
                const flattenedTags = tags.flat().filter((tag) => tag !== "");
                if (flattenedTags.length === 0) {
                    return schema.required("Tag is required.");
                }
                return schema.notRequired();
            })
            .matches(/^[a-zA-Z0-9\s]*$/, "Tag can only contain letters and numbers."),
        status: Yup.string().required("Status is required."),
        fk_mag_key: Yup.string().required("Magazine is required."),
        category: Yup.array()
            .of(Yup.string().required("Category is required."))
            .min(1, "At least one category must be selected.")
            .required("Category is required."),
    });

    async function checkArticleTitle(ac_name) {
        const response = await axios.post(BASE_URL + "API/apicall", {
            security_key: security_key,
            mode: "checkArticleTitle",
            pk_ac_key: id,
            ac_name: ac_name,
        });

        const output = response.data;

        return output == 1 ? false : true;
    }

    const formik = useFormik({
        initialValues,
        validationSchema,
        enableReinitialize: true,
        onSubmit: async (formValues) => {
            if (formValues.tags.length === 0) {
                formik.setFieldError("tags", "Please add at least one tag before submitting the article.");
                return;
            }
            toast.dismiss();
            setIsSubmitting(true);
            toast.dismiss();
            setIsSubmitting(true);

            const response = await axios.post(
                `${BASE_URL}API/apicall`,
                {
                    security_key,
                    mode: "updateRssFeed",
                    ...formValues,
                },
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            const output = response.data;
            if (output == 1) {
                fetchArticle();
                toast.success("Updated successfully!", {
                    autoClose: 3000,
                });

                setTimeout(() => {
                    navigate("../rss-management");
                }, 3000);
            } else {
                toast.error("Oops! Something went wrong.", {
                    autoClose: 3000,
                });
            }

            setIsSubmitting(false);
        },
    });

    // Now, you can set variables as needed:
    const values = formik.values;
    const handleBlur = formik.handleBlur;
    const touched = formik.touched;
    const handleChange = formik.handleChange;
    const errors = formik.errors;
    const handleSubmit = formik.handleSubmit;
    const setFieldValue = formik.setFieldValue;
    const setTouched = formik.setTouched;
    const resetForm = formik.resetForm;
    const setValues = formik.setValues;
    const handleReset = formik.handleReset;

    const handleAddTag = () => {
        if (values.newTag && !values.tags.flat().includes(values.newTag)) {
            setFieldValue("tags", [...values.tags.flat(), values.newTag]); // Flatten to avoid nested arrays
            setFieldValue("newTag", "");
            if (newTagRef.current) {
                newTagRef.current.focus();
                newTagRef.current.blur();
            }
        }
    };

    const handleRemoveTag = (tagToRemove) => {
        setFieldValue(
            "tags",
            values.tags.filter((tag) => tag !== tagToRemove)
        );
    };

    const handleFileChange = (e, index) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.addEventListener("load", () => {
                setFieldValue("ac_img_link", reader.result);
            });
            reader.readAsDataURL(file);
            setFieldValue("ac_img_new", file);
        }
    };

    const handlePreview = () => {
        getFullArticle(values.ac_content);
        setShowPreview(true); // Show the preview modal
    };

    const handleClosePreview = () => {
        setShowPreview(false); // Hide the preview modal
    };

    return (
        <>
            <ToastContainer position="top-center" />
            <div className="container mt-4 bg-white p-4 mb-3">
                <Form className="mb-3" onSubmit={handleSubmit} ref={formRef}>
                    <Form.Group className="d-flex justify-content-between mb-3 flex-sm-wrap">
                        <h4 className="mb-2">Edit Article</h4>
                        <div>
                            <Button variant="outline-dark" className="me-2" onClick={handlePreview}>
                                Preview
                            </Button>

                            <Button variant="dark" type="submit" disabled={isSubmitting}>
                                Save Article
                                {isSubmitting && <LinearProgress />}
                            </Button>
                        </div>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>
                            Title <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control type="text" name="ac_name" value={values.ac_name} onChange={handleChange} onBlur={handleBlur} placeholder="Title" />
                        {errors.ac_name && touched.ac_name && <p className="text-danger">{errors.ac_name}</p>}
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>
                            Summary <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3} // Set the number of rows for the textarea
                            name="ac_summary"
                            value={values.ac_summary}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            placeholder="Summary"
                        />
                        {errors.ac_summary && touched.ac_summary && <p className="text-danger">{errors.ac_summary}</p>}
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>
                            Content Link <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                            type="text"
                            name="ac_content"
                            value={values.ac_content}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            placeholder="Enter content link"
                        />
                        {errors.ac_content && touched.ac_content && <p className="text-danger">{errors.ac_content}</p>}
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>
                            Image <span className="text-danger">*</span>
                        </Form.Label>
                        <label htmlFor={`additionalImage`} role="button" className="dropzone border p-4 rounded w-100 text-center">
                            <p>Drag 'n' drop an image here, or click to select one</p>
                            <img src={values.ac_img_link} alt="Additional" style={{ maxWidth: "100%", maxHeight: "200px", objectFit: "cover" }} />
                        </label>
                        <input type="file" id="additionalImage" onChange={handleFileChange} onBlur={handleBlur} style={{ display: "none" }} accept="image/*" />
                        {errors.ac_img_new && touched.ac_img_new && <p className="text-danger">{errors.ac_img_new}</p>}
                    </Form.Group>

                    <div className="mb-3 d-flex gap-2 flex-sm-wrap flex-md-nowrap">
                        <Form.Group controlId="status" className="w-50 w-sm-100 w-md-100">
                            <Form.Label>
                                Status <span className="text-danger">*</span>
                            </Form.Label>
                            <Select
                                options={statusOption}
                                id="status"
                                name="status"
                                value={statusOption.find((option) => option.value && values.status && option.value.toString() === values.status.toString())}
                                onChange={(selectedOption) => {
                                    const event = {
                                        target: {
                                            name: "status",
                                            value: selectedOption["value"],
                                        },
                                    };
                                    handleChange(event);
                                }}
                                onBlur={(event) => {
                                    handleBlur(event);
                                    setTouched({ ...touched, status: true });
                                }}
                            />

                            {errors.status && touched.status && <p className="text-danger">{errors.status}</p>}
                        </Form.Group>
                        <Form.Group controlId="category" className="w-50  w-sm-100 w-md-100">
                            <Form.Label>
                                Category <span className="text-danger">*</span>{" "}
                            </Form.Label>
                            <Select
                                options={categoryOption}
                                id="category"
                                name="category"
                                value={categoryOption.filter((option) => values.category.includes(option.value))}
                                isMulti
                                onChange={(selectedOptions) => {
                                    const event = {
                                        target: {
                                            name: "category",
                                            value: selectedOptions.map((option) => option.value), // Map to an array of selected values
                                        },
                                    };
                                    handleChange(event);
                                }}
                                onBlur={(event) => {
                                    handleBlur(event);
                                    setTouched({ ...touched, category: true });
                                }}
                            />

                            {errors.category && touched.category && <p className="text-danger">{errors.category}</p>}
                        </Form.Group>
                        <Form.Group controlId="fk_mag_key" className="w-50  w-sm-100 w-md-100">
                            <Form.Label>
                                Magazine <span className="text-danger">*</span>{" "}
                            </Form.Label>
                            <Select
                                options={magazineOption}
                                id="fk_mag_key"
                                name="fk_mag_key"
                                value={magazineOption.filter((option) => values.fk_mag_key.includes(option.value))}
                                onChange={(selectedOption) => {
                                    const event = {
                                        target: {
                                            name: "fk_mag_key",
                                            value: selectedOption["value"],
                                        },
                                    };
                                    handleChange(event);
                                }}
                                onBlur={(event) => {
                                    handleBlur(event);
                                    setTouched({ ...touched, fk_mag_key: true });
                                }}
                            />

                            {errors.fk_mag_key && touched.fk_mag_key && <p className="text-danger">{errors.fk_mag_key}</p>}
                        </Form.Group>
                    </div>
                    <div className="mb-3 d-flex gap-2">
                        <Form.Group controlId="newTag" className="w-50">
                            <Form.Label>
                                Tag <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                                type="text"
                                ref={newTagRef}
                                value={values.newTag}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                placeholder="Add a tag"
                                className="form-control border"
                            />
                            {errors.newTag && touched.newTag && <p className="text-danger">{errors.newTag}</p>}
                            {errors.tags && touched.tags && <p className="text-danger">{errors.tags}</p>}
                        </Form.Group>

                        <div className="w-50">
                            <Button
                                variant="outline-dark"
                                onClick={handleAddTag}
                                id="addTagBtn"
                                className="p-0 p-1"
                                style={{ fontSize: "14px", marginTop: "30px" }}
                                disabled={!!errors.newTag && touched.newTag}
                            >
                                Add Tag
                            </Button>
                        </div>
                    </div>
                    {values.tags.length > 0 && (
                        <div className="mb-3">
                            <h6>Tags:</h6>
                            {values.tags.map((tag) => (
                                // <span key={tag} className="badge bg-secondary me-2 position-relative" style={{ cursor: "pointer" }}>
                                //     {tag}
                                //     <span
                                //         className="position-absolute translate-middle"
                                //         style={{
                                //             color: "red",
                                //             top: "-3px",
                                //             end: "-3",
                                //             fontSize: "1rem",
                                //             cursor: "pointer",
                                //             padding: "0.2rem",
                                //             zIndex: 1,
                                //         }}
                                //         onClick={(e) => {
                                //             e.stopPropagation(); // Prevents triggering the parent onClick
                                //             handleRemoveTag(tag);
                                //         }}
                                //     >
                                //         ×
                                //     </span>
                                // </span>
                                <span key={tag} className="badge bg-secondary me-4 position-relative" style={{ cursor: "pointer" }}>
                                    {tag}
                                    <span
                                        className="position-absolute translate-middle"
                                        style={{
                                            color: "red",
                                            top: "7px",
                                            right: "-25px",
                                            fontSize: "1rem",
                                            cursor: "pointer",
                                            padding: "0.2rem",
                                            zIndex: 1,
                                        }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleRemoveTag(tag);
                                        }}
                                    >
                                        ×
                                    </span>
                                </span>
                            ))}
                        </div>
                    )}
                </Form>

                {/* Preview Modal */}
                <Modal show={showPreview} onHide={handleClosePreview} size="lg">
                    <Modal.Header closeButton>
                        <Modal.Title>Article Preview</Modal.Title>
                    </Modal.Header>
                    <Modal.Body style={{ height: "800px", overflowY: "scroll" }}>
                        <h1 className="text-center">{values.ac_name}</h1>
                        <h5>{values.ac_summary}</h5>
                        {values.ac_img_link && <img src={values.ac_img_link} className="rounded mx-auto d-block mb-3" alt="..." />}

                        {values.ac_content && (
                            <div style={{ height: "600px" }}>
                                <iframe
                                    src={
                                        values.ac_content.includes("youtube.com") || values.ac_content.includes("youtu.be")
                                            ? values.ac_content
                                            : "https://thepaperboy.net/API/article_iframe.php?url=" + values.ac_content
                                    }
                                    title="Article Content"
                                    style={{
                                        width: "100%",
                                        height: "100%",
                                        border: "none",
                                    }}
                                    allowFullScreen
                                />
                            </div>
                        )}

                        {/* <div>
                            <div
                                // style={{
                                //     position: "relative",
                                //     width: "100%", // or a specific width like '500px'
                                //     height: "500px", // or 'auto' or any specific height
                                //     overflowY: "auto", // enables vertical scrolling if content overflows
                                //     border: "1px solid #ccc", // optional: add a border for visibility
                                //     padding: "10px", // optional: add padding for better readability
                                //     boxSizing: "border-box", // includes padding and border in the element's total width and height
                                // }}
                                dangerouslySetInnerHTML={{ __html: fullArticle }}
                            />
                        </div> */}

                        {values.tags.length > 0 && (
                            <div className="mt-3">
                                <h6>Tags:</h6>
                                {values.tags.map((tag) => (
                                    <span key={tag} className="badge bg-secondary me-2">
                                        {tag}
                                    </span>
                                ))}
                            </div>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleClosePreview}>
                            Close
                        </Button>
                    </Modal.Footer>
                </Modal>
            </div>
        </>
    );
};

const RichTextEditor = {
    modules: {
        toolbar: [
            [{ header: "1" }, { header: "2" }, { font: [] }],
            [{ list: "ordered" }, { list: "bullet" }],
            ["bold", "italic", "underline"],
            [{ align: [] }],
            // ["link", "image"],
            ["clean"],
        ],
    },
    formats: ["header", "font", "list", "bullet", "bold", "italic", "underline", "align", "link", "image", "clean"],
};

export default EditRssArticleForm;
